import { Capacitor } from '@capacitor/core';
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';

/**
 * Custom URL scheme used by the native iOS/Android app for deep links.
 * Must match the schemes registered in Info.plist (iOS) and AndroidManifest (Android).
 */
export const APP_URL_SCHEME = 'mypocketcoach';

/**
 * Returns the redirect URL to use for Supabase password-reset emails.
 * Always returns the native custom scheme — password reset is iOS-only.
 * The web fallback is intentionally removed.
 */
export function getPasswordResetRedirectUrl(): string {
  return `${APP_URL_SCHEME}://reset-password`;
}

/**
 * Parses a deep link URL and extracts Supabase recovery tokens.
 * Supports both hash (#access_token=...) and query (?access_token=...) styles.
 */
export function parseRecoveryTokens(url: string): {
  access_token?: string;
  refresh_token?: string;
  type?: string;
  error?: string;
  error_description?: string;
} {
  try {
    const u = new URL(url);
    // Supabase usually returns tokens in the fragment.
    const hash = u.hash.startsWith('#') ? u.hash.slice(1) : u.hash;
    const params = new URLSearchParams(hash || u.search);
    return {
      access_token: params.get('access_token') ?? undefined,
      refresh_token: params.get('refresh_token') ?? undefined,
      type: params.get('type') ?? undefined,
      error: params.get('error') ?? undefined,
      error_description: params.get('error_description') ?? undefined,
    };
  } catch {
    return {};
  }
}

let listenerInstalled = false;

/**
 * Installs the Capacitor App URL listener. When iOS opens the app via the
 * password-reset deep link, we set the recovery session and navigate to
 * /reset-password so the user can choose a new password — all inside the app.
 */
export function initDeepLinkHandler(navigate: (path: string) => void): () => void {
  if (!Capacitor.isNativePlatform() || listenerInstalled) {
    return () => {};
  }
  listenerInstalled = true;

  const handle = async (event: URLOpenListenerEvent) => {
    const url = event.url || '';
    if (!url.includes('reset-password') && !url.includes('type=recovery')) {
      return;
    }

    const tokens = parseRecoveryTokens(url);

    if (tokens.error) {
      navigate(`/reset-password?error=${encodeURIComponent(tokens.error)}`);
      return;
    }

    if (tokens.access_token && tokens.refresh_token) {
      try {
        await supabase.auth.setSession({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
        });
      } catch (err) {
        console.error('[deepLinks] setSession failed', err);
      }
    }

    navigate('/reset-password');
  };

  const handlePromise = App.addListener('appUrlOpen', handle);

  return () => {
    handlePromise.then((h) => h.remove()).catch(() => {});
    listenerInstalled = false;
  };
}
